import { useEffect, useState, useCallback } from 'react'
import { useParams, Link } from 'react-router-dom'
import { supabase } from '../../lib/supabase'
import { colors, formatNumber, formatPercent } from '../../styles/tokens'
import { Select } from '../../components/FormControls'
import { ErrorBanner, SuccessBanner, SkeletonRows, EmptyState } from '../../components/Feedback'
import EstimationBreadcrumb from '../../components/EstimationBreadcrumb'
import * as XLSX from 'xlsx'

// Cost Control review layer for the Indirect Budget (Normal Indirect +
// Risk & Contingency + HO Overhead). No financial calculation happens
// here: every Tender Budget/Rate value is read verbatim from the same
// SSOTs the Indirect input page already uses -- indirect_cost_item,
// risk_contingency_line, ho_overhead_setting, and
// estimate_revision_calculation_chain for the Risk/HO calculated
// amounts. Editable here: Cost Package, Department/Category, Cost
// Code -- classification only.
//
// KNOWN LIMITATION (acknowledged, out of scope for this change): this
// page always reads the live Draft tables. It does not yet switch to
// the official frozen/snapshot path the way Direct Budget Review does.
// Export Excel therefore only supports the current Draft Revision.
export default function IndirectBudgetReviewPage() {
  const { tenderId, revisionId } = useParams()
  const [items, setItems] = useState(null)
  const [riskLines, setRiskLines] = useState(null)
  const [hoSetting, setHoSetting] = useState(null)
  const [calcChain, setCalcChain] = useState(null)
  const [costCodes, setCostCodes] = useState([])
  const [error, setError] = useState(null)
  const [successMsg, setSuccessMsg] = useState(null)
  const [expandedPkgs, setExpandedPkgs] = useState(new Set())
  const [expandedRows, setExpandedRows] = useState(new Set())
  const [savingRow, setSavingRow] = useState(null)
  const [isFrozenRevision, setIsFrozenRevision] = useState(false)

  const load = useCallback(async () => {
    setError(null)
    // Draft/Frozen resolution -- same pattern Direct Budget Review
    // uses: check estimate_revision.status first, then read from the
    // matching official source. Frozen reads ONLY from the existing
    // freeze_snapshot.full_indirect_data structure (risk_lines,
    // ho_overhead_setting, site_overhead_items) -- never from the
    // live tables for a frozen revision. No new snapshot mechanism,
    // no new financial calculation: this only changes WHERE the same
    // shaped data comes from.
    const revRes = await supabase.from('estimate_revision').select('status').eq('id', revisionId).maybeSingle()
    const isFrozen = revRes.data?.status === 'frozen'

    if (isFrozen) {
      const [snapRes, ccRes] = await Promise.all([
        supabase.from('freeze_snapshot').select('full_indirect_data, direct_cost_total, site_overhead_total, ho_overhead_total, risk_contingency_total')
          .eq('estimate_revision_id', revisionId).maybeSingle(),
        supabase.from('canonical_cost_code').select('id, code, description'),
      ])
      if (snapRes.error) return setError(snapRes.error.message)
      const snap = snapRes.data?.full_indirect_data || {}
      // Historical snapshots taken before package_name/department/
      // canonical_cost_code_id/qty/unit/rate existed simply won't have
      // these keys -- left as-is (undefined/null), never invented.
      setItems(snap.site_overhead_items || [])
      setRiskLines(snap.risk_lines || [])
      setHoSetting(snap.ho_overhead_setting || null)
      setCalcChain({
        direct_cost_total: snapRes.data?.direct_cost_total,
        general_contingency_total: snapRes.data?.risk_contingency_total,
        ho_overhead_total: snapRes.data?.ho_overhead_total,
      })
      setCostCodes(ccRes.data || [])
      setIsFrozenRevision(true)
      return
    }

    setIsFrozenRevision(false)
    const [itemsRes, riskRes, hoRes, chainRes, ccRes] = await Promise.all([
      supabase.from('indirect_cost_item').select('*').eq('estimate_revision_id', revisionId),
      supabase.from('risk_contingency_line').select('*').eq('estimate_revision_id', revisionId),
      supabase.from('ho_overhead_setting').select('*').eq('estimate_revision_id', revisionId).maybeSingle(),
      supabase.from('estimate_revision_calculation_chain').select('general_contingency_total, ho_overhead_total, direct_cost_total').eq('estimate_revision_id', revisionId).maybeSingle(),
      supabase.from('canonical_cost_code').select('id, code, description'),
    ])
    if (itemsRes.error) return setError(itemsRes.error.message)
    setItems(itemsRes.data || [])
    setRiskLines(riskRes.data || [])
    setHoSetting(hoRes.data || null)
    setCalcChain(chainRes.data || null)
    setCostCodes(ccRes.data || [])
  }, [revisionId])

  useEffect(() => { load() }, [load])

  // Classification-only update (package_name / department / cost_code).
  // Never touches qty, rate, amount, pct, or any calculation field.
  // Classification now goes exclusively through the three dedicated
  // SECURITY DEFINER RPCs (approved authorization design) instead of a
  // direct table UPDATE -- each RPC checks Tender access AND the
  // IndirectBudget.ClassifyIndirect permission, and only ever touches
  // canonical_cost_code_id/department, regardless of what table/field
  // this handler is called with.
  const CLASSIFY_RPC_BY_TABLE = {
    indirect_cost_item: 'classify_indirect_cost_item',
    risk_contingency_line: 'classify_risk_contingency_line',
    ho_overhead_setting: 'classify_ho_overhead_setting',
  }
  const CLASSIFY_ID_PARAM_BY_TABLE = {
    indirect_cost_item: 'p_item_id',
    risk_contingency_line: 'p_line_id',
    ho_overhead_setting: 'p_setting_id',
  }

  async function handleClassify(table, id, field, value) {
    // Classification edits target the live tables and have no
    // meaning against a frozen snapshot; guard rather than write to
    // rows that may not even exist by that id shape in the snapshot.
    if (isFrozenRevision) return
    setSavingRow(id + field)
    setError(null)
    const rpcName = CLASSIFY_RPC_BY_TABLE[table]
    const idParam = CLASSIFY_ID_PARAM_BY_TABLE[table]
    const params = { [idParam]: id }
    if (field === 'canonical_cost_code_id') {
      params.p_canonical_cost_code_id = value
      params.p_clear_cost_code = value == null
    } else if (field === 'department') {
      params.p_department = value
      params.p_clear_department = value == null
    }
    const { error } = await supabase.rpc(rpcName, params)
    if (error) setError(error.message)
    else { setSuccessMsg('Saved.'); await load() }
    setSavingRow(null)
  }

  function togglePkg(key) {
    setExpandedPkgs((prev) => { const n = new Set(prev); n.has(key) ? n.delete(key) : n.add(key); return n })
  }
  function toggleRow(id) {
    setExpandedRows((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n })
  }

  if (items === null) return <SkeletonRows rows={4} cols={4} />

  // Per-activity Tender Budget, read from the same fields the input
  // page already uses -- no recalculation. unit/indirect_budget_rate
  // are set explicitly (Normal Indirect items already carry a real
  // unit and rate) so every row shape is consistent across the three
  // Cost Types feeding the export below -- this is what prevents
  // undefined/NaN cells in the generated Excel.
  const indirectRows = items.map((i) => ({
    ...i,
    tender_budget: i.cost_nature === 'time_related' ? (Number(i.qty) || 0) * (Number(i.monthly_rate) || 0) : (Number(i.fixed_amount) || 0),
    indirect_budget_rate: i.indirect_budget_rate ?? (i.cost_nature === 'time_related' ? i.monthly_rate : null),
    unit: i.unit || (i.cost_nature === 'time_related' ? 'month' : null),
  }))

  // Risk: per-line amount is either its own fixed `amount`, or its
  // share of the authoritative general_contingency_total via
  // pct_of_direct_cost (same formula the calculation_chain view uses).
  const directCostTotal = Number(calcChain?.direct_cost_total || 0)
  const riskRows = (riskLines || []).map((l) => {
    const budget = l.amount != null ? Number(l.amount) : (Number(l.pct_of_direct_cost || 0) / 100) * directCostTotal
    const months = Object.keys(l.planned_monthly_distribution || {})
    return {
      ...l,
      tender_budget: budget,
      qty: months.length || null,
      unit: months.length > 0 ? 'month' : null,
      indirect_budget_rate: months.length > 0 ? budget / months.length : null,
    }
  })

  const hoTotal = Number(calcChain?.ho_overhead_total || 0)

  const groupByPackage = (rows, costTypeLabel) => {
    const groups = {}
    for (const r of rows) {
      const key = r.package_name || '(No Cost Package)'
      if (!groups[key]) groups[key] = []
      groups[key].push(r)
    }
    return Object.entries(groups).map(([pkg, rows]) => ({
      packageName: pkg,
      costType: costTypeLabel,
      rows,
      total: rows.reduce((s, r) => s + r.tender_budget, 0),
      monthlyTotals: rows.reduce((acc, r) => {
        for (const [m, v] of Object.entries(r.planned_monthly_distribution || {})) acc[m] = (acc[m] || 0) + Number(v || 0)
        return acc
      }, {}),
    }))
  }

  const indirectPackages = groupByPackage(indirectRows, 'Normal Indirect')
  const riskPackages = groupByPackage(riskRows, 'Risk & Contingency')
  const hoMonths = hoSetting ? Object.keys(hoSetting.planned_monthly_distribution || {}) : []
  const hoRow = hoSetting ? {
    ...hoSetting,
    description: 'HO Overhead',
    qty: hoMonths.length || null,
    unit: hoMonths.length > 0 ? 'month' : null,
    indirect_budget_rate: hoMonths.length > 0 ? hoTotal / hoMonths.length : null,
    tender_budget: hoTotal,
  } : null
  const hoPackages = hoRow ? groupByPackage([hoRow], 'HO Overhead') : []

  const grandTotal = indirectPackages.reduce((s, p) => s + p.total, 0)
    + riskPackages.reduce((s, p) => s + p.total, 0)
    + hoPackages.reduce((s, p) => s + p.total, 0)

  // Excel serial date for the 1st of a 'YYYY-MM' key (Excel epoch
  // 1899-12-30). CostSpace's importer requires real date cells for
  // month columns (proven from parseTenderExcel), not text like
  // "Feb-26". Package keys are already YYYY-MM (from
  // planned_monthly_distribution); this only affects the exported
  // Excel cell type, never the underlying stored month keys.
  // Excel serial date for a 'Mon-YY' key (e.g. 'Feb-26'), matching the
  // actual format stored in planned_monthly_distribution throughout
  // this app (proven from every Indirect/Risk/HO record inspected --
  // NOT 'YYYY-MM'). Excel epoch is 1899-12-30. Returns null on any
  // unrecognized key instead of NaN, so a malformed key produces a
  // blank cell rather than corrupting the workbook XML.
  function monthKeyToExcelSerial(key) {
    const monthNames = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec']
    const parts = String(key).split('-')
    if (parts.length !== 2) return null
    const [monAbbr, yy] = parts
    const monIdx = monthNames.indexOf(monAbbr)
    if (monIdx === -1 || !/^\d{2}$/.test(yy)) return null
    const fullYear = 2000 + Number(yy)
    const excelEpoch = Date.UTC(1899, 11, 30)
    const target = Date.UTC(fullYear, monIdx, 1)
    return Math.round((target - excelEpoch) / 86400000)
  }

  // Builds the Excel workbook for the CURRENT DRAFT revision only,
  // using the exact same package/row data already computed and
  // rendered above. Package Header row = SUM(Activity Tender Budget)
  // and SUM(Activity monthly values) per month -- required because
  // the proven CostSpace importer only persists monthly_data from
  // Package rows and only aggregates Package tender_budget into
  // Budget Summary (Activity rows are financially inert there).
  // Nothing here is written back to Estimation; this is a read-only
  // export of already-computed display values.
  // True chronological sort for 'Mon-YY' keys -- JS default Array.sort()
  // is alphabetical ("Apr-24" < "Apr-26" is correct by luck, but e.g.
  // "Oct-26" < "Sep-24" alphabetically, which is wrong). This mirrors
  // the same sortMonthKeys helper already used on IndirectRiskHoPage.
  function sortMonthKeys(keys) {
    const monthOrder = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec']
    return [...keys].sort((a, b) => {
      const [ma, ya] = a.split('-')
      const [mb, yb] = b.split('-')
      if (ya !== yb) return Number(ya) - Number(yb)
      return monthOrder.indexOf(ma) - monthOrder.indexOf(mb)
    })
  }

  function handleExportExcel() {
    // Guards every numeric cell against NaN/Infinity/undefined --
    // these are display representations only; if a value is not a
    // finite number, the cell is written blank rather than as text
    // "NaN"/"Infinity" (which is what caused Excel's repair prompt).
    const safeNum = (v) => (typeof v === 'number' && Number.isFinite(v)) ? v : ''

    const allMonthKeys = new Set()
    const collectMonths = (pkgs) => pkgs.forEach((p) => Object.keys(p.monthlyTotals).forEach((m) => allMonthKeys.add(m)))
    collectMonths(indirectPackages); collectMonths(riskPackages); collectMonths(hoPackages)
    const sortedMonths = sortMonthKeys(Array.from(allMonthKeys))

    // CostSpace's real parser detects month columns from the ROW
    // BELOW the text header (monthRow = raw[headerIdx+1]), scanning
    // for numeric values > 40000 -- NOT from the header row itself.
    // Row 0 must therefore stay pure text/blank in the month columns;
    // Row 1 carries the actual Excel date serials, and real data
    // starts at Row 2. This was proven directly from the production
    // parser's console debug output (dates written in Row 0 were
    // read as arbitrary numeric cell values from Row 1 instead,
    // producing garbage years).
    const headers = ['Item No.', 'Description', 'Package/Activity', 'Cost Code', 'Department / Category',
      'Qty', 'Unit', 'Rate', 'Tender Budget', 'Total Months', ...sortedMonths.map(() => '')]
    const monthRow = ['', '', '', '', '', '', '', '', '', '', ...sortedMonths.map(() => '')] // placeholder; real dates written as cells below

    const rows = []
    let itemNo = 0

    const emitCostType = (packages, prefix) => {
      packages.forEach((pkg) => {
        itemNo++
        const pkgLabel = `[${prefix}] — ${pkg.packageName}`
        const pkgQtyMax = pkg.rows.length ? Math.max(...pkg.rows.map((r) => Number(r.qty) || 0)) : 0
        rows.push([
          itemNo, pkgLabel, 'package', '', '',
          '', '', '', safeNum(pkg.total), safeNum(pkgQtyMax),
          ...sortedMonths.map((m) => safeNum(pkg.monthlyTotals[m] || 0)),
        ])
        pkg.rows.forEach((r) => {
          const cc = costCodes.find((c) => c.id === r.canonical_cost_code_id)
          const dist = r.planned_monthly_distribution || {}
          rows.push([
            itemNo, r.description, 'activity', cc?.code || '', r.department || '',
            safeNum(Number(r.qty)), r.unit || '', safeNum(Number(r.indirect_budget_rate)), safeNum(Number(r.tender_budget)),
            safeNum(Number(r.qty)) || 0,
            ...sortedMonths.map((m) => (dist[m] != null ? safeNum(Number(dist[m])) : '')),
          ])
        })
      })
    }
    emitCostType(indirectPackages, 'NORMAL INDIRECT')
    emitCostType(riskPackages, 'RISK & CONTINGENCY')
    emitCostType(hoPackages, 'HO OVERHEAD')

    const ws = XLSX.utils.aoa_to_sheet([headers, monthRow, ...rows])
    // Write the real Excel date serials into ROW 1 (the row the
    // production parser actually scans), not Row 0.
    const monthColStart = 10 // 0-indexed: after the 10 fixed columns
    sortedMonths.forEach((_, mi) => {
      const col = monthColStart + mi
      const dateCellRef = XLSX.utils.encode_cell({ r: 1, c: col })
      const serial = monthKeyToExcelSerial(sortedMonths[mi])
      // Guard: only write a numeric date cell when conversion
      // succeeded. Otherwise leave the cell blank -- never write a
      // NaN numeric cell, which is what corrupted the workbook XML.
      if (serial != null) ws[dateCellRef] = { t: 'n', v: serial, z: 'mmm-yy' }
    })
    ws['!cols'] = [{ wch: 8 }, { wch: 34 }, { wch: 10 }, { wch: 12 }, { wch: 16 }, { wch: 8 }, { wch: 8 }, { wch: 10 }, { wch: 14 }, { wch: 10 },
      ...sortedMonths.map(() => ({ wch: 10 }))]

    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, ws, 'Indirect Budget')
    XLSX.writeFile(wb, `indirect_budget_${tenderId}_${revisionId}.xlsx`)
  }

  return (
    <div>
      <EstimationBreadcrumb to={`/estimation/tender/${tenderId}/revision/${revisionId}/lifecycle`} label={'\u2190 Back to Validation Gate'} />
      <h1 style={{ fontSize: 22, fontWeight: 700, color: colors.textPrimary, margin: '10px 0 4px', display: 'flex', alignItems: 'center', gap: 10 }}>
        Indirect Budget Review
        {isFrozenRevision && (
          <span style={{ fontSize: 12, fontWeight: 700, padding: '3px 10px', borderRadius: 6, background: '#1E293B', color: '#FFF' }}>
            🔒 Frozen Snapshot
          </span>
        )}
      </h1>
      <p style={{ fontSize: 13, color: colors.textSecondary, margin: '0 0 24px' }}>
        {isFrozenRevision
          ? 'Read-only official frozen snapshot. Classification editing is disabled for frozen revisions.'
          : 'Cost Control review and coding layer for Normal Indirect, Risk & Contingency, and HO Overhead. Values are read-only from the Estimation SSOT; only Cost Package, Department, and Cost Code are editable here.'}
      </p>

      <ErrorBanner message={error} />
      <SuccessBanner message={successMsg} />

      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', marginBottom: 24, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <div style={{ fontSize: 11, color: colors.textMuted, textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 4 }}>Total Indirect Budget</div>
          <div style={{ fontSize: 22, fontWeight: 700 }}>{formatNumber(grandTotal)} SAR</div>
        </div>
        {/* Export is available for both Draft and Frozen. For a
            frozen revision, indirectRows/riskRows/hoRow above are
            already sourced from load()'s Frozen branch --
            freeze_snapshot.full_indirect_data -- so this button
            exports exactly the frozen state, never the live tables. */}
        <button onClick={handleExportExcel}
          title={isFrozenRevision ? 'Exports the frozen snapshot as it was frozen' : undefined}
          style={{ padding: '10px 16px', background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
          📥 Export Excel
        </button>
      </div>

      <CostTypeBlock title="Normal Indirect" packages={indirectPackages} table="indirect_cost_item" costCodes={costCodes}
        expandedPkgs={expandedPkgs} togglePkg={togglePkg} expandedRows={expandedRows} toggleRow={toggleRow}
        onClassify={handleClassify} savingRow={savingRow} showRate />

      <div style={{ height: 24 }} />

      <CostTypeBlock title="Risk & Contingency" packages={riskPackages} table="risk_contingency_line" costCodes={costCodes}
        expandedPkgs={expandedPkgs} togglePkg={togglePkg} expandedRows={expandedRows} toggleRow={toggleRow}
        onClassify={handleClassify} savingRow={savingRow} showRate showBasis />

      <div style={{ height: 24 }} />

      <CostTypeBlock title="HO Overhead" packages={hoPackages} table="ho_overhead_setting" costCodes={costCodes}
        expandedPkgs={expandedPkgs} togglePkg={togglePkg} expandedRows={expandedRows} toggleRow={toggleRow}
        onClassify={handleClassify} savingRow={savingRow} showRate showBasis singleActivityNote />
    </div>
  )
}

function CostTypeBlock({ title, packages, table, costCodes, expandedPkgs, togglePkg, expandedRows, toggleRow, onClassify, savingRow, showRate, showBasis, singleActivityNote }) {
  const total = packages.reduce((s, p) => s + p.total, 0)
  return (
    <section>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, marginBottom: 10 }}>
        <h2 style={{ fontSize: 16, fontWeight: 700, color: colors.textPrimary, margin: 0 }}>{title}</h2>
        <span style={{ fontSize: 13, color: colors.textMuted }}>{formatNumber(total)} SAR</span>
      </div>
      {packages.length === 0 ? (
        <div style={{ padding: 16, background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: 8 }}>
          <EmptyState title={`No ${title} activities yet`} />
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {packages.map((pkg) => {
            const pkgKey = `${table}:${pkg.packageName}`
            const isOpen = expandedPkgs.has(pkgKey)
            return (
              <div key={pkgKey} style={{ background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: 8, overflow: 'hidden' }}>
                <div onClick={() => togglePkg(pkgKey)} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', cursor: 'pointer', background: '#FAFBFC', borderBottom: isOpen ? `1px solid ${colors.border}` : 'none' }}>
                  <span>{isOpen ? '▾' : '▸'}</span>
                  <span style={{ fontWeight: 700, fontSize: 13 }}>{pkg.packageName}</span>
                  <span style={{ fontSize: 12, color: colors.textMuted }}>({pkg.rows.length} {pkg.rows.length === 1 ? 'activity' : 'activities'})</span>
                  <span style={{ marginLeft: 'auto', fontWeight: 700, fontSize: 13 }}>{formatNumber(pkg.total)} SAR</span>
                </div>
                {isOpen && (
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, minWidth: 900 }}>
                      <thead>
                        <tr style={{ background: '#FAFBFC', borderBottom: `1px solid ${colors.border}` }}>
                          <Th></Th><Th>Description</Th><Th align="right">Qty</Th><Th>Unit</Th>
                          {showRate && <Th align="right">Rate</Th>}
                          {showBasis && <Th align="right">Basis</Th>}
                          <Th align="right">Tender Budget</Th><Th>Cost Code</Th><Th>Department / Category</Th><Th>Distribution</Th>
                        </tr>
                      </thead>
                      <tbody>
                        {pkg.rows.map((row) => {
                          const dist = row.planned_monthly_distribution || {}
                          const months = Object.keys(dist)
                          const distSum = Object.values(dist).reduce((s, v) => s + (Number(v) || 0), 0)
                          const reconciled = months.length === 0 || Math.abs(distSum - row.tender_budget) <= 0.01
                          const rowOpen = expandedRows.has(row.id)
                          return (
                            <>
                              <tr key={row.id} style={{ borderBottom: `1px solid ${colors.border}` }}>
                                <Td>{months.length > 0 && <span style={{ cursor: 'pointer' }} onClick={() => toggleRow(row.id)}>{rowOpen ? '▾' : '▸'}</span>}</Td>
                                <Td>{row.description}{singleActivityNote && <span style={{ color: colors.textMuted, fontSize: 11 }}> (single calculated activity)</span>}</Td>
                                <Td align="right">{row.qty != null ? formatNumber(row.qty) : '—'}</Td>
                                <Td muted>{row.unit || '—'}</Td>
                                {showRate && <Td align="right">{(row.indirect_budget_rate ?? row.monthly_rate) != null ? formatNumber(row.indirect_budget_rate ?? row.monthly_rate) : '—'}</Td>}
                                {showBasis && <Td align="right" muted>{row.pct_of_direct_cost != null ? formatPercent(row.pct_of_direct_cost) : (row.ho_overhead_pct != null ? formatPercent(row.ho_overhead_pct) : '—')}</Td>}
                                <Td align="right">{formatNumber(row.tender_budget)}</Td>
                                <Td>
                                  <Select value={row.canonical_cost_code_id || ''} disabled={savingRow === row.id + 'canonical_cost_code_id'}
                                    onChange={(e) => onClassify(table, row.id, 'canonical_cost_code_id', e.target.value || null)} style={{ fontSize: 12, minWidth: 140 }}>
                                    <option value="">⚠ Not assigned</option>
                                    {costCodes.map((cc) => <option key={cc.id} value={cc.id}>{cc.code} — {cc.description}</option>)}
                                  </Select>
                                </Td>
                                <Td>
                                  <input defaultValue={row.department || ''} placeholder="—"
                                    onBlur={(e) => e.target.value !== (row.department || '') && onClassify(table, row.id, 'department', e.target.value || null)}
                                    style={{ fontSize: 12, width: 130, border: `1px solid ${colors.border}`, borderRadius: 4, padding: '4px 6px' }} />
                                </Td>
                                <Td>
                                  {months.length === 0 ? (
                                    <span style={{ color: '#92400E', fontSize: 12 }}>⚠ None</span>
                                  ) : reconciled ? (
                                    <span style={{ color: '#065F46', fontSize: 12 }}>✅ {months.length} mo</span>
                                  ) : (
                                    <span style={{ color: '#991B1B', fontSize: 12 }}>🚫 Mismatch</span>
                                  )}
                                </Td>
                              </tr>
                              {rowOpen && months.length > 0 && (
                                <tr><td colSpan={10} style={{ padding: '0 0 12px 24px' }}>
                                  <div style={{ display: 'flex', gap: 8, overflowX: 'auto' }}>
                                    {months.map((m) => (
                                      <div key={m} style={{ flex: '0 0 auto', minWidth: 80, textAlign: 'center', fontSize: 11 }}>
                                        <div style={{ color: colors.textMuted }}>{m}</div>
                                        <div style={{ fontWeight: 600 }}>{formatNumber(dist[m])}</div>
                                      </div>
                                    ))}
                                  </div>
                                </td></tr>
                              )}
                            </>
                          )
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}
    </section>
  )
}

function Th({ children, align = 'left' }) {
  return <th style={{ textAlign: align, padding: '8px 12px', fontSize: 11, fontWeight: 600, color: colors.textSecondary, textTransform: 'uppercase', letterSpacing: 0.3, whiteSpace: 'nowrap' }}>{children}</th>
}
function Td({ children, align = 'left', muted }) {
  return <td style={{ textAlign: align, padding: '9px 12px', fontSize: 13, color: muted ? colors.textMuted : colors.textPrimary, whiteSpace: 'nowrap' }}>{children}</td>
}
